import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { getShowViewerBreakdown, getShowViewerByFingerprint, postShowViewer } from 'api/api'

import Constants from 'constants.js'
import { RootState } from 'redux/store'
import * as safeApi from 'api'
import { Show } from 'data/Show'
import { ShowViewer } from 'data/ShowViewer'
import _ from 'lodash'
import { createSelector } from 'reselect'
import { generateDeviceFingerprint } from 'fingerprint.js'
import moment from 'moment'
import numeral from 'numeral'
import { showError } from './globalError'
import { isSuccess } from 'data/ApiResult'

interface ViewerCountByCountry {
    viewerCount: number
    country: {
        emoji: string
        name: string
        code: string
    }
}

export type ViewerBreakdown = ViewerCountByCountry[]

interface ViewerState {
    count: number
    showViewer: ShowViewer | null
    viewerBreakdown: ViewerBreakdown
}

interface CounterUpdatedPayload {
    viewerCount: number
}

interface ViewerRegisteredPayload {
    viewerCount: number
    showViewer: ShowViewer
}

const defaultViewer = { previewStartedAt: new Date().toISOString() }

export const initialState: ViewerState = {
    count: 0,
    showViewer: null,
    viewerBreakdown: [],
}

const viewerSlice = createSlice({
    name: 'viewer',
    initialState,
    reducers: {
        resetState: _ => initialState,
        viewerRegistered: (state, { payload }: PayloadAction<ViewerRegisteredPayload>) => ({
            ...state,
            count: payload.viewerCount,
            showViewer: payload.showViewer,
        }),
        counterUpdated: (state, { payload: viewerCount }: PayloadAction<number>) => ({
            ...state,
            count: viewerCount,
        }),
        viewerBreakdownReceived: (state, { payload }: PayloadAction<{ breakdown: ViewerBreakdown }>) => ({
            ...state,
            viewerBreakdown: payload.breakdown,
        }),
    },
})

const { actions, reducer } = viewerSlice
export default reducer

const normalizeCounter = (count = 0) => (count >= 1000 ? numeral(count).format('0.0a') : count)

const getViewerState = (store: RootState): ViewerState => store.viewer

export const getViewerCounter = createSelector(getViewerState, viewer => normalizeCounter(viewer.count))
export const getShowViewer = createSelector(getViewerState, viewer => viewer.showViewer || defaultViewer)

export const getShowViewerStartTime = createSelector(getShowViewer, showViewer =>
    moment(showViewer.previewStartedAt).format(),
)

export const getViewerBreakdown = createSelector(getViewerState, viewer => viewer.viewerBreakdown)

export const { resetState, viewerRegistered, counterUpdated, viewerBreakdownReceived } = actions

export const registerViewer = (show: Show, wsChannel) => async dispatch => {
    try {
        const fingerprint = await generateDeviceFingerprint()
        const { viewer, viewerCount } = await getShowViewerByFingerprint({ showId: show._id, fingerprint })
        const showViewer = getShowViewerPatch(viewer, show.startTime)
        await dispatch(viewerRegistered({ viewerCount, showViewer }))
    } catch (err) {
        if (_.get(err, 'response.status') === 404) {
            const isWithinTimeConstraint = moment(show.startTime)
                .subtract(2, 'hours')
                .isBefore(moment())

            if (isWithinTimeConstraint) {
                const { viewerCount, showViewer: viewer } = await postShowViewer(show, wsChannel)
                const showViewer = getShowViewerPatch(viewer, show.startTime)

                dispatch(viewerRegistered({ viewerCount, showViewer }))
            }
        } else {
            dispatch(
                showError(
                    "There's nothing worse than a technical difficulty at a concert, but even live streams can have them. Please try refreshing the page, or try again in a few minutes.",
                ),
            )
        }
    }
}

// If we load the page when the show has started the free preview starts with some seconds less than the 2 minutes expected.
// That's because we are getting the value that calculates the time before the animation finishes loading.
// TODO: in the future we should probably find a better way to solve this issue
export const getShowViewerPatch = (showViewer: ShowViewer, showStartTime: string): ShowViewer => {
    const newShowViewer = { ...showViewer }
    const isAfterShowStarted = moment().isSameOrAfter(showStartTime)
    if (isAfterShowStarted) {
        const seconds = Constants.SET_PAGE_TIME_TO_SET_ANIMATION_COMPLETE_IN_MS / 1000
        newShowViewer.previewStartedAt = moment(showViewer.previewStartedAt)
            .clone()
            .add(seconds, 'seconds')
            .format()
    }

    return newShowViewer
}

export const fetchViewerBreakdown = (showId: string) => async dispatch => {
    try {
        const breakdown = await getShowViewerBreakdown(showId)
        dispatch(viewerBreakdownReceived(breakdown))
    } catch (error) {
        console.error(error)
    }
}

export const fetchViewerCounter = (showId: string) => async dispatch => {
    const result = await safeApi.getViewerCount(showId)

    if (isSuccess(result)) {
        dispatch(counterUpdated(result.data))
    }
}
